import { useReducer, useCallback } from "react";

const SET_STATE = "SET_STATE";
const UNDO = "UNDO";
const REDO = "REDO";
const CLEAR = "CLEAR";

const reducerWithUndoRedo = (state, action) => {
    const { past, present, future } = state;

    switch (action.type) {
        case SET_STATE:
            {
                // Only add to history if the state actually changed
                if (JSON.stringify(present) === JSON.stringify(action.data)) {
                    return state;
                }

                // Create a snapshot that preserves individual row additions
                const newPast = [...past];

                // If we're adding to an existing category, only store the incremental change
                if (present.length > 0 && action.data.length > 0) {
                    const lastCategory = present[present.length - 1];
                    const newCategory = action.data[action.data.length - 1];

                    // If same category but different row count, create incremental snapshot
                    if (lastCategory && newCategory &&
                        lastCategory.category === newCategory.category &&
                        lastCategory.rows.length < newCategory.rows.length) {

                        // Store state before this row was added
                        const incrementalState = [...action.data];
                        incrementalState[incrementalState.length - 1] = {
                            ...newCategory,
                            rows: newCategory.rows.slice(0, -1) // Remove the last added row
                        };

                        newPast.push(incrementalState.filter(cat => cat.rows.length > 0));
                    } else {
                        newPast.push(present);
                    }
                } else {
                    newPast.push(present);
                }

                // Limit history size
                const maxHistorySize = 100;
                const limitedPast = newPast.length > maxHistorySize
                    ? newPast.slice(-maxHistorySize)
                    : newPast;

                return {
                    past: limitedPast,
                    present: action.data,
                    future: []
                };
            }
        case UNDO:
            {
                if (past.length === 0) return state;
                const previous = past[past.length - 1];
                const newPast = past.slice(0, -1);

                return {
                    past: newPast,
                    present: previous,
                    future: [present, ...future]
                };
            }
        case REDO:
            {
                if (future.length === 0) return state;
                const next = future[0];
                const newFuture = future.slice(1);

                return {
                    past: [...past, present],
                    present: next,
                    future: newFuture
                };
            }
        case CLEAR:
            {
                return {
                    past: [],
                    present: action.initialState || [],
                    future: []
                };
            }
        default:
            throw new Error();
    }
};

const useUndoRedo = (initialState = []) => {
    const [state, dispatch] = useReducer(reducerWithUndoRedo, {
        past: [],
        present: initialState,
        future: []
    });
    const { past, present, future } = state;

    const setState = useCallback((newState) => {
        dispatch({ type: SET_STATE, data: newState });
    }, []);

    const undo = useCallback(() => {
        if (past.length > 0) {
            dispatch({ type: UNDO });
            return past[past.length - 1];
        }
        return present;
    }, [past, present]);

    const redo = useCallback(() => {
        if (future.length > 0) {
            dispatch({ type: REDO });
            return future[0];
        }
        return present;
    }, [future, present]);

    const clear = useCallback((initialStateParam = []) => {
        dispatch({ type: CLEAR, initialState: initialStateParam });
    }, []);

    const isUndoPossible = past && past.length > 0;
    const isRedoPossible = future && future.length > 0;

    return {
        state: present,
        setState,
        undo,
        redo,
        clear,
        pastStates: past,
        futureStates: future,
        isUndoPossible,
        isRedoPossible
    };
};

export default useUndoRedo;